import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"

import { useGetAccountsQuery } from "../accounts/accountsApiSlice"
import { useGetContactsQuery } from "../contacts/contactsApiSlice"
import {
  useGetAccountContactMapsQuery,
  useCreateNewAccountContactMapMutation,
} from "./accountContactMapsApiSlice"

const EtCreateAccountContactMap = ({ setActiveAdminView, userOrgId = "" }) => {
  // First Created - 27 Apr 2026
  // Author - Atul Pandey
  // Purpose - To add a Contact to an Account Contact Map
  // Updated - 28 Apr 2026 -  refined for reportsToContactId

  useTitle("Create Account Contact Map")

  const navigate = useNavigate()

  const [selectedAccountId, setSelectedAccountId] = useState("")

  const [createNewAccountContactMap, { isLoading, isSuccess, isError, error }] =
    useCreateNewAccountContactMapMutation()

  const { data: accountsData, isLoading: isAccountsLoading } =
    useGetAccountsQuery({ ownerOrgId: userOrgId })

  const { data: contactsData, isLoading: isContactsLoading } =
    useGetContactsQuery()

  const { data: accountContactMapsData } = useGetAccountContactMapsQuery(
    { accountId: selectedAccountId },
    { skip: !selectedAccountId }
  )

//   const { data: accountContactMapsData } = useGetAccountContactMapsQuery()

  const accounts = accountsData?.ids?.map(id => accountsData?.entities[id]) || []
  const contacts = contactsData?.ids?.map(id => contactsData?.entities[id]) || []
  
  console.log('accounts:', accounts)
  console.log('contacts:', contacts)
  
  const accountContactMaps =
    accountContactMapsData?.ids?.map(id => accountContactMapsData?.entities[id]) || []

  const selectedAccount = accounts.find(
    account => (account?._id || account?.id) === selectedAccountId
  )

  const selectedAccountContactOrgId =
    selectedAccount?.contactOrganizationId?._id ||
    selectedAccount?.contactOrganizationId?.id ||
    selectedAccount?.contactOrganizationId ||
    ""

  const mappedContactIds = accountContactMaps
    ?.map(item => item?.contactId?._id || item?.contactId?.id || item?.contactId)
    ?.filter(Boolean)

  const eligibleContacts = contacts?.filter(contact => {
    const contactOrgId =
      contact?.contactOrgId?._id ||
      contact?.contactOrgId?.id ||
      contact?.contactOrgId ||
      ""

    const contactId = contact?._id || contact?.id

    return (
      contactOrgId?.toString() === selectedAccountContactOrgId?.toString() &&
      !mappedContactIds?.includes(contactId)
    )
  })

  const [formData, setFormData] = useState({
    accountId: "",
    contactId: "",
    reportsToContactId: "", // 28 Apr 2026
    reportsToAccountContactMapId: "",
    internalOwnerUserId: "",
    contactMapRole: "",
    buyingRole: "",
    influenceLevel: "",
    relationshipStrength: "Unknown",
    relationshipSentiment: "Unknown",
    conversationCadence: "",
    lastMeaningfulConversationDate: "",
    nextPlannedConversationDate: "",
    strategicImportance: "",
    accessLevel: "NoAccess",
    contactMapNotes: [{ noteText: "", noteDate: "", addedByUserId: "" }],
  })

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      accountId: selectedAccountId,
      contactId: "",
      reportsToContactId: "", // 28 Apr 2026
      reportsToAccountContactMapId: "",
    }))
  }, [selectedAccountId])

  useEffect(() => {
    if (isSuccess) {
      setFormData({
        accountId: "",
        contactId: "",
        reportsToContactId: "", // 28 Apr 2026
        reportsToAccountContactMapId: "",
        internalOwnerUserId: "",
        contactMapRole: "",
        buyingRole: "",
        influenceLevel: "",
        relationshipStrength: "Unknown",
        relationshipSentiment: "Unknown",
        conversationCadence: "",
        lastMeaningfulConversationDate: "",
        nextPlannedConversationDate: "",
        strategicImportance: "",
        accessLevel: "NoAccess",
        contactMapNotes: [{ noteText: "", noteDate: "", addedByUserId: "" }],
      })

      if (setActiveAdminView) {
        setActiveAdminView("accountContactMaps")
      } else {
        navigate("/accountContactMaps")
      }
    }
  }, [isSuccess])

  const handleChange = e => {
    const { name, value } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleAddNote = () => {
    setFormData(prev => ({
      ...prev,
      contactMapNotes: [
        ...prev.contactMapNotes,
        { noteText: "", noteDate: "", addedByUserId: "" },
      ],
    }))
  }

  const handleRemoveNote = index => {
    setFormData(prev => ({
      ...prev,
      contactMapNotes: prev.contactMapNotes.filter((_, i) => i !== index),
    }))
  }

  const handleNoteChange = (index, fieldName, value) => {
    const updatedNotes = formData.contactMapNotes.map((note, i) =>
      i === index ? { ...note, [fieldName]: value } : note
    )

    setFormData(prev => ({
      ...prev,
      contactMapNotes: updatedNotes,
    }))
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const cleanedNotes = formData.contactMapNotes.filter(
      note => note.noteText?.trim()
    )

    const payload = {
      accountId: formData.accountId,
      contactId: formData.contactId,
      reportsToContactId: formData.reportsToContactId || null, // 28 Apr 2026
      reportsToAccountContactMapId:
        formData.reportsToAccountContactMapId || null,
      internalOwnerUserId: formData.internalOwnerUserId || null,
      contactMapRole: formData.contactMapRole,
      buyingRole: formData.buyingRole,
      influenceLevel: formData.influenceLevel,
      relationshipStrength: formData.relationshipStrength,
      relationshipSentiment: formData.relationshipSentiment,
      conversationCadence: formData.conversationCadence,
      lastMeaningfulConversationDate:
        formData.lastMeaningfulConversationDate || null,
      nextPlannedConversationDate:
        formData.nextPlannedConversationDate || null,
      strategicImportance: formData.strategicImportance,
      accessLevel: formData.accessLevel,
      contactMapNotes: cleanedNotes,
    }

    try {
      await createNewAccountContactMap(payload).unwrap()
    } catch (error) {
      console.error(error)
    }
  }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("accountContactMaps")
    } else {
      navigate("/accountContactMaps")
    }
  }

  if (isAccountsLoading || isContactsLoading) {
    return <PulseLoader color={"#ea580c"} />
  }

  return (
    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Add Contact to Account Map
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
            <select
              name="accountId"
              value={selectedAccountId}
              onChange={e => setSelectedAccountId(e.target.value)}
              className="w-full rounded p-2 m-1"
            >
              <option value="">Select Account</option>
              {accounts?.map(account => (
                <option key={account?._id || account?.id} value={account?._id || account?.id}>
                  {account?.accountName}
                </option>
              ))}
            </select>

            {selectedAccount && (
              <div className="bg-white rounded m-1 p-2">
                <p className="text-[10px] text-gray-600">
                  Client Org:{" "}
                  <span className="font-bold">
                    {selectedAccount?.contactOrganizationId?.contactOrganizationName ||
                      "Not linked"}
                  </span>
                </p>
              </div>
            )}

            <select
              name="contactId"
              value={formData.contactId}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
              disabled={!selectedAccountId}
            >
              <option value="">Select Contact</option>
              {eligibleContacts?.map(contact => (
                <option key={contact?._id || contact?.id} value={contact?._id || contact?.id}>
                  {contact?.contactName} {contact?.contactEmail ? `(${contact.contactEmail})` : ""}
                </option>
              ))}
            </select>

            <select
                name="reportsToContactId"
                value={formData.reportsToContactId}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
                disabled={!selectedAccountId}
            >
            <option value="">Reports To Contact</option>
            {contacts
                ?.filter(contact => {
                const contactOrgId =
                    contact?.contactOrgId?._id ||
                    contact?.contactOrgId?.id ||
                    contact?.contactOrgId ||
                    ""

                const contactId = contact?._id || contact?.id

                return (
                    contactOrgId?.toString() === selectedAccountContactOrgId?.toString() &&
                    contactId?.toString() !== formData.contactId?.toString()
                )
                })
                ?.map(contact => (
                <option key={contact?._id || contact?.id} value={contact?._id || contact?.id}>
                    {contact?.contactName} {contact?.contactEmail ? `(${contact.contactEmail})` : ""}
                </option>
                ))}
            </select>

            <select
              name="reportsToAccountContactMapId"
              value={formData.reportsToAccountContactMapId}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
              disabled={!selectedAccountId}
            >
              <option value="">Reports To Existing Account Map Entry</option>
              {accountContactMaps?.map(mapEntry => (
                <option key={mapEntry?._id || mapEntry?.id} value={mapEntry?._id || mapEntry?.id}>
                  {mapEntry?.contactId?.contactName || "Mapped Contact"}
                </option>
              ))}
            </select>

            <input
              type="text"
              name="contactMapRole"
              placeholder="Contact map role e.g. Sponsor, Champion, Blocker"
              value={formData.contactMapRole}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />

            <input
              type="text"
              name="buyingRole"
              placeholder="Buying role e.g. Economic Buyer, Technical Buyer"
              value={formData.buyingRole}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />

            <div className="grid grid-cols-2 gap-1">
              <select
                name="influenceLevel"
                value={formData.influenceLevel}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >
                <option value="">Influence Level</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Very High">Very High</option>
              </select>

              <select
                name="relationshipStrength"
                value={formData.relationshipStrength}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >
                <option value="Unknown">Unknown</option>
                <option value="Weak">Weak</option>
                <option value="Developing">Developing</option>
                <option value="Strong">Strong</option>
                <option value="Trusted">Trusted</option>
              </select>

              <select
                name="relationshipSentiment"
                value={formData.relationshipSentiment}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >
                <option value="Unknown">Unknown</option>
                <option value="Negative">Negative</option>
                <option value="Neutral">Neutral</option>
                <option value="Positive">Positive</option>
                <option value="Very Positive">Very Positive</option>
              </select>

              <select
                name="conversationCadence"
                value={formData.conversationCadence}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >
                <option value="">Conversation Cadence</option>
                <option value="Weekly">Weekly</option>
                <option value="BiWeekly">BiWeekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="AdHoc">AdHoc</option>
              </select>

              <select
                name="strategicImportance"
                value={formData.strategicImportance}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >
                <option value="">Strategic Importance</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>

              <select
                name="accessLevel"
                value={formData.accessLevel}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >
                <option value="NoAccess">No Access</option>
                <option value="LimitedAccess">Limited Access</option>
                <option value="DirectAccess">Direct Access</option>
                <option value="TrustedAccess">Trusted Access</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-1">
              <input
                type="date"
                name="lastMeaningfulConversationDate"
                value={formData.lastMeaningfulConversationDate}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />

              <input
                type="date"
                name="nextPlannedConversationDate"
                value={formData.nextPlannedConversationDate}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div className="m-1 p-1 border border-white rounded">
              <div>
                <p className="font-bold mx-2 px-2 mt-2">Contact Map Notes</p>
                <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
                  Add any relationship context for this contact.
                </p>
              </div>

              {formData.contactMapNotes.map((note, index) => (
                <div key={index} className="grid grid-cols-12 gap-1 items-center mx-1">
                  <input
                    type="text"
                    value={note.noteText}
                    onChange={e =>
                      handleNoteChange(index, "noteText", e.target.value)
                    }
                    placeholder="Add note"
                    className="col-span-8 rounded p-2 m-1 text-xs"
                  />

                  <input
                    type="date"
                    value={note.noteDate}
                    onChange={e =>
                      handleNoteChange(index, "noteDate", e.target.value)
                    }
                    className="col-span-3 rounded p-2 m-1 text-xs"
                  />

                  <div className="col-span-1 rounded bg-red-500 m-1 p-1 flex justify-center">
                    <button type="button" onClick={() => handleRemoveNote(index)}>
                      <span className="text-white text-xs font-bold">-</span>
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex justify-start items-center m-1">
                <p className="font-bold text-xs m-1 p-1">Add Note</p>
                <button
                  type="button"
                  onClick={handleAddNote}
                  className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500 flex justify-center items-center"
                >
                  +
                </button>
              </div>
            </div>

            <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Create"}
                type={"create"}
                handleClick={handleSubmit}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EtCreateAccountContactMap